import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';

/** #1256 E-Mail(server)management — lokale SMTP-Grundeinstellungen. Passwort
 * liegt verschlüsselt im Backend; das Frontend erhält nur `hasPassword`. */
export interface IMailSettings {
  idMailSetting: number;
  SmtpServer?: string | null;
  SmtpPort?: string | null;
  Encryption: string;
  Username?: string | null;
  hasPassword: boolean;
  FromAddress?: string | null;
  FromName?: string | null;
  ConnectionStatus: string;
  LastTest_at?: string | null;
  LastError?: string | null;
  isLocallyConfigured: boolean;
  ConfigOrigin: string;
  SenderPolicy: string;
  SenderCustomAddress?: string | null;
  SenderCustomName?: string | null;
  SenderRewriteMode: string;
}

export interface IMailSettingsUpdate {
  SmtpServer?: string | null;
  SmtpPort?: string | null;
  Encryption?: string;
  Username?: string | null;
  Password?: string;
  FromAddress?: string | null;
  FromName?: string | null;
  SenderPolicy?: string;
  SenderCustomAddress?: string | null;
  SenderCustomName?: string | null;
  SenderRewriteMode?: string;
}

/** Ein Benutzer im Prüfumfang der gewählten Absender-Regel. */
export interface IMailSenderUser {
  idUser: number;
  Name: string;
  Email: string;
  isNotary: boolean;
  hasEmail: boolean;
  hasSignature: boolean;
  Groups: string[];
}

/** Eine Mitarbeitergruppe im Prüfumfang (Gruppenpostfach als Absender). */
export interface IMailSenderGroup {
  idUserGroup: number;
  Name: string;
  Email: string;
  hasEmail: boolean;
  hasSignature: boolean;
  MemberCount: number;
}

/**
 * Durchgerechnetes Beispiel zur gewählten Regel — mit den tatsächlich
 * hinterlegten Benutzern und Gruppen. Zeigt, was der Mandant im Postfach sieht;
 * geht die Kette nicht auf, nennt `Warning` die Adresse, die dann sendet.
 */
export interface IMailSenderExample {
  Severity: 'info' | 'warning';
  /** Die angenommene Lage, z. B. „Im Vorgang ist die Gruppe … hinterlegt." */
  Case: string;
  /** Name und Adresse getrennt, damit die Adresse herausgestellt werden kann. */
  FromName?: string | null;
  FromAddress?: string | null;
  /** Zusatz zum Ergebnis, z. B. „Antworten gehen an das Gruppenpostfach." */
  ResultNote?: string | null;
  Signature?: string | null;
  Warning?: string | null;
}

/** Erläuterung zur Absender-Regel + Signatur-Status der betroffenen Benutzer. */
export interface IMailSenderInfo {
  SenderPolicy: string;
  Label: string;
  Description: string;
  TransportHint: string;
  ScopeLabel?: string | null;
  checksSignatures: boolean;
  SignatureHint: string;
  InternalMailHint?: string | null;
  SenderRewriteMode: string;
  RewriteHint?: string | null;
  Example?: IMailSenderExample | null;
  Users: IMailSenderUser[];
  UserGroups: IMailSenderGroup[];
  Counts: {
    total: number;
    withSignature: number;
    withoutSignature: number;
    withoutEmail: number;
    blockedSkipped: number;
  };
}

export const useMailSettings = () => {
  const { data: mailSettings, isLoading: isLoadingMailSettings } =
    useQuery<IMailSettings>({
      queryKey: ['mailSetting'],
      queryFn: () => customFetcher({ endpoint: '/mailsetting' }),
    });
  return { mailSettings, isLoadingMailSettings };
};

export const useUpdateMailSettings = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateMailSettings, isPending: isUpdatingMailSettings } =
    useMutation({
      mutationFn: (body: IMailSettingsUpdate) =>
        customFetcher({ endpoint: '/mailsetting', method: 'PUT', body }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['mailSetting'] }),
    });
  return { updateMailSettings, isUpdatingMailSettings };
};

export const useTestMailConnection = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: testMailConnection,
    isPending: isTestingMailConnection,
  } = useMutation({
    mutationFn: (body: IMailSettingsUpdate) =>
      customFetcher({ endpoint: '/mailsetting/test', method: 'POST', body }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['mailSetting'] }),
  });
  return { testMailConnection, isTestingMailConnection };
};

export const useSendTestMail = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: sendTestMail, isPending: isSendingTestMail } =
    useMutation({
      mutationFn: (body: IMailSettingsUpdate & { recipient?: string }) =>
        customFetcher({
          endpoint: '/mailsetting/testmail',
          method: 'POST',
          body,
        }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['mailSetting'] }),
    });
  return { sendTestMail, isSendingTestMail };
};

/** Erläuterung + Signatur-Prüfung zur (auch noch ungespeicherten) Absender-Regel. */
export const useMailSenderInfo = (policy: string) => {
  const {
    data: mailSenderInfo,
    isFetching: isLoadingMailSenderInfo,
    refetch: refetchMailSenderInfo,
  } = useQuery<IMailSenderInfo>({
    queryKey: ['mailSenderInfo', policy],
    queryFn: () =>
      customFetcher({
        endpoint: `/mailsetting/senderinfo?policy=${encodeURIComponent(policy)}`,
      }),
  });
  return { mailSenderInfo, isLoadingMailSenderInfo, refetchMailSenderInfo };
};

export const useResetMailSettings = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: resetMailSettings, isPending: isResettingMailSettings } =
    useMutation({
      mutationFn: () =>
        customFetcher({ endpoint: '/mailsetting/reset', method: 'POST' }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['mailSetting'] }),
    });
  return { resetMailSettings, isResettingMailSettings };
};
